import React, { useState, useEffect } from 'react';
import axios from 'axios';
import './FinanceModuleStyles.css';
import { FileText, Plus, Search, Edit, Trash2, X, Calendar, DollarSign, Send, CheckCircle, Printer } from 'lucide-react';

const API_URL = import.meta.env.VITE_API_URL || '';

const QuotationModule = () => {
  const [quotations, setQuotations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [editingQuotation, setEditingQuotation] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [formData, setFormData] = useState({
    quotationNumber: '',
    date: '',
    clientName: '',
    clientPin: '',
    description: '',
    items: [{ description: '', quantity: 1, rate: 0 }],
    taxRate: 0,
    validUntil: '',
    status: 'draft'
  });

  useEffect(() => { fetchQuotations(); }, []);

  const fetchQuotations = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/quotations`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Transform data to ensure proper types
      const data = response.data?.data || [];
      const transformedData = data.map(item => ({
        ...item,
        totalAmount: parseFloat(item.totalAmount) || 0,
        date: item.date ? new Date(item.date).toISOString().split('T')[0] : '',
        validUntil: item.validUntil ? new Date(item.validUntil).toISOString().split('T')[0] : ''
      }));
      
      setQuotations(transformedData);
    } catch (error) { console.error('Error:', error); }
    setLoading(false);
  };

  const handleItemChange = (index, field, value) => {
    const newItems = [...formData.items];
    newItems[index][field] = value;
    setFormData(prev => ({ ...prev, items: newItems }));
  };

  const addItem = () => setFormData(prev => ({ ...prev, items: [...prev.items, { description: '', quantity: 1, rate: 0 }] }));
  const removeItem = (index) => {
    if (formData.items.length > 1) setFormData(prev => ({ ...prev, items: prev.items.filter((_, i) => i !== index) }));
  };

  const calculateTotals = () => {
    const subtotal = formData.items.reduce((sum, item) => sum + (parseFloat(item.quantity) * parseFloat(item.rate) || 0), 0);
    const taxAmount = (subtotal * (parseFloat(formData.taxRate) || 0)) / 100;
    return { subtotal, taxAmount, total: subtotal + taxAmount };
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const { subtotal, taxAmount, total } = calculateTotals();
      const quotationData = { ...formData, subtotal, taxAmount, totalAmount: total };
      if (editingQuotation) {
        await axios.put(`${API_URL}/quotations/${editingQuotation._id}`, quotationData, { headers: { Authorization: `Bearer ${token}` } });
      } else {
        await axios.post(`${API_URL}/quotations`, quotationData, { headers: { Authorization: `Bearer ${token}` } });
      }
      setShowModal(false); setEditingQuotation(null); resetForm(); fetchQuotations();
    } catch (error) { alert('Failed to save quotation'); }
    setLoading(false);
  };

  const handleEdit = (quotation) => {
    setEditingQuotation(quotation);
    setFormData({
      quotationNumber: quotation.quotationNumber || '',
      date: quotation.date || '',
      clientName: quotation.clientName || '',
      clientPin: quotation.clientPin || '',
      description: quotation.description || '',
      items: quotation.items || [{ description: '', quantity: 1, rate: 0 }],
      taxRate: quotation.taxRate || 0,
      validUntil: quotation.validUntil || '',
      status: quotation.status || 'draft'
    });
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (!confirm('Delete this quotation?')) return;
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API_URL}/quotations/${id}`, { headers: { Authorization: `Bearer ${token}` } });
      fetchQuotations();
    } catch (error) { alert('Failed to delete'); }
  };

  const handleStatusChange = async (id, newStatus) => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API_URL}/quotations/${id}/status`, { status: newStatus }, { headers: { Authorization: `Bearer ${token}` } });
      fetchQuotations();
    } catch (error) { console.error('Error:', error); }
  };

  const handlePrint = (quotation) => {
    const printWindow = window.open('', '_blank');
    const formatCurrency = (amount) => `KES ${Number(amount || 0).toLocaleString('en-GB', { minimumFractionDigits: 2 })}`;
    const formatDate = (dateString) => {
      if (!dateString) return 'N/A';
      try {
        return new Date(dateString).toLocaleDateString('en-GB');
      } catch (e) { return 'N/A'; }
    };
    const items = quotation.items || [];
    
    printWindow.document.write(`
      <html>
      <head>
        <title>Quotation ${quotation.quotationNumber}</title>
        <style>
          body { font-family: 'Segoe UI', Arial, sans-serif; margin: 0; padding: 40px; color: #1e293b; }
          .quote-container { max-width: 800px; margin: 0 auto; border: 2px solid #e2e8f0; border-radius: 12px; padding: 40px; }
          .header { text-align: center; margin-bottom: 30px; border-bottom: 3px solid #667eea; padding-bottom: 20px; }
          .logo { width: 80px; height: 80px; border-radius: 50%; margin-bottom: 10px; }
          .company-name { font-size: 20px; font-weight: 700; color: #0f172a; margin: 0; }
          .company-tagline { font-size: 12px; color: #64748b; margin: 4px 0; }
          .quote-title { font-size: 24px; font-weight: 800; color: #667eea; margin: 20px 0; letter-spacing: 4px; }
          .quote-number { font-size: 14px; color: #64748b; font-weight: 600; }
          .info-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 20px; margin: 20px 0; }
          .info-card { background: #f8fafc; border-radius: 8px; padding: 16px; }
          .info-card h4 { margin: 0 0 10px 0; color: #334155; font-size: 13px; text-transform: uppercase; }
          .info-row { display: flex; justify-content: space-between; padding: 6px 0; border-bottom: 1px solid #e2e8f0; }
          .info-row:last-child { border-bottom: none; }
          .info-label { font-weight: 600; color: #475569; font-size: 12px; }
          .info-value { color: #334155; font-size: 12px; }
          table { width: 100%; border-collapse: collapse; margin: 20px 0; }
          th { background: linear-gradient(135deg, #667eea 0%, #764ba2 100%); color: white; padding: 12px; text-align: left; font-size: 12px; text-transform: uppercase; }
          td { padding: 12px; border-bottom: 1px solid #f1f5f9; font-size: 13px; }
          .amount-cell { text-align: right; font-family: 'Courier New', monospace; font-weight: 600; }
          .summary-box { background: #f8fafc; border-radius: 10px; padding: 20px; margin: 20px 0; border: 1px solid #e2e8f0; }
          .summary-row { display: flex; justify-content: space-between; padding: 8px 0; border-bottom: 1px solid #e2e8f0; }
          .summary-row.total { font-size: 16px; font-weight: 700; color: #0f172a; border-top: 2px solid #334155; border-bottom: 2px solid #334155; margin-top: 8px; }
          .footer { margin-top: 30px; padding-top: 20px; border-top: 2px solid #e2e8f0; text-align: center; }
          .footer p { margin: 4px 0; font-size: 12px; color: #64748b; }
          .stamp-area { width: 100px; height: 100px; border: 2px dashed #dc2626; border-radius: 50%; display: flex; align-items: center; justify-content: center; margin: 20px auto; transform: rotate(-15deg); opacity: 0.6; }
          .stamp-area span { color: #dc2626; font-size: 10px; font-weight: 700; text-transform: uppercase; text-align: center; }
          .validity { background: #fef3c7; border-left: 4px solid #f59e0b; padding: 12px; margin: 20px 0; border-radius: 6px; }
          .validity p { margin: 0; color: #92400e; font-size: 13px; font-weight: 600; }
          @media print { body { padding: 0; } .quote-container { border: none; } }
        </style>
      </head>
      <body>
        <div class="quote-container">
          <div class="header">
            <img src="https://www.secs.gismac.co.ke/logos/gismac.jpg" class="logo" alt="Gismac Security" />
            <h1 class="company-name">Gismac Security Limited</h1>
            <p class="company-tagline">Professional Security Solutions</p>
            <div class="quote-title">QUOTATION</div>
            <div class="quote-number">#${quotation.quotationNumber || 'N/A'}</div>
          </div>
          
          <div class="info-grid">
            <div class="info-card">
              <h4>Client Information</h4>
              <div class="info-row"><span class="info-label">Name:</span><span class="info-value">${quotation.clientName || 'N/A'}</span></div>
              <div class="info-row"><span class="info-label">PIN:</span><span class="info-value">${quotation.clientPin || '-'}</span></div>
            </div>
            <div class="info-card">
              <h4>Quotation Details</h4>
              <div class="info-row"><span class="info-label">Date:</span><span class="info-value">${formatDate(quotation.quotation_date || quotation.date)}</span></div>
              <div class="info-row"><span class="info-label">Valid Until:</span><span class="info-value">${formatDate(quotation.validUntil)}</span></div>
              <div class="info-row"><span class="info-label">Status:</span><span class="info-value">${quotation.status || 'Draft'}</span></div>
            </div>
          </div>
          
          <table>
            <thead>
              <tr>
                <th>#</th>
                <th>Description</th>
                <th>Qty</th>
                <th>Rate (KES)</th>
                <th class="amount-cell">Amount (KES)</th>
              </tr>
            </thead>
            <tbody>
              ${items.map((item, idx) => `
                <tr>
                  <td>${idx + 1}</td>
                  <td>${item.description || 'Item'}</td>
                  <td>${item.quantity || 1}</td>
                  <td class="amount-cell">${formatCurrency(item.rate || 0).replace('KES ', '')}</td>
                  <td class="amount-cell">${formatCurrency((item.quantity || 1) * (item.rate || 0)).replace('KES ', '')}</td>
                </tr>
              `).join('')}
            </tbody>
          </table>
          
          <div class="summary-box">
            <div class="summary-row"><span>Subtotal:</span><span>${formatCurrency(quotation.subtotal || 0)}</span></div>
            <div class="summary-row"><span>Tax (${quotation.taxRate || 0}%):</span><span>${formatCurrency(quotation.tax_amount || quotation.taxAmount || 0)}</span></div>
            <div class="summary-row total"><span>Total Amount:</span><span>${formatCurrency(quotation.total_amount || quotation.totalAmount || 0)}</span></div>
          </div>
          
          <div class="validity">
            <p>⏰ This quotation is valid until ${formatDate(quotation.validUntil)}. Prices are subject to change after expiry.</p>
          </div>
          
          <div class="stamp-area"><span>OFFICIAL<br>STAMP</span></div>
          
          <div class="footer">
            <p><strong>Thank you for considering Gismac Security!</strong></p>
            <p>📧 accounts@gismac.co.ke | 🌐 www.secs.gismac.co.ke</p>
            <p>📍 Nairobi, Kenya</p>
            <p style="margin-top: 10px; font-size: 10px; color: #94a3b8;">Generated by Gismac Security Management System</p>
          </div>
        </div>
        <script>window.onload = () => { setTimeout(() => window.print(), 500); };</script>
      </body>
      </html>
    `);
    printWindow.document.close();
  };

  const resetForm = () => setFormData({
    quotationNumber: '', date: '', clientName: '', clientPin: '', description: '', items: [{ description: '', quantity: 1, rate: 0 }], taxRate: 0, validUntil: '', status: 'draft'
  });

  const formatCurrency = (amount) => {
    const num = parseFloat(amount);
    if (isNaN(num) || amount === null || amount === undefined) return 'KES 0.00';
    return `KES ${num.toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const formatDate = (dateString) => {
    if (!dateString || dateString === 'null' || dateString === 'undefined') return 'N/A';
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return 'N/A';
      return date.toLocaleDateString('en-GB');
    } catch (e) { return 'N/A'; }
  };

  const filteredQuotations = quotations.filter(q =>
    q.quotationNumber?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    q.clientName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    q.description?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const { subtotal, taxAmount, total } = calculateTotals();

  return (
    <div className="finance-module">
      <div className="module-header">
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
          <img src="/logos/gismac.jpg" alt="GISMAC" style={{ width: '60px', height: '60px', objectFit: 'contain', borderRadius: '50%' }} />
          <div>
            <h2><FileText size={24} /> Quotation Management</h2>
            <p className="module-subtitle">Create and manage client quotations</p>
          </div>
        </div>
        <button className="btn btn-primary" onClick={() => { setEditingQuotation(null); resetForm(); setShowModal(true); }}>
          <Plus size={18} /> New Quotation
        </button>
      </div>

      <div className="stats-row">
        <div className="stat-box blue">
          <div className="stat-icon"><DollarSign size={20} /></div>
          <div className="stat-info">
            <span className="stat-value">{formatCurrency(quotations.reduce((sum, q) => sum + (parseFloat(q.totalAmount) || 0), 0))}</span>
            <span className="stat-label">Total Value</span>
          </div>
        </div>
        <div className="stat-box teal">
          <div className="stat-icon"><FileText size={20} /></div>
          <div className="stat-info">
            <span className="stat-value">{quotations.length}</span>
            <span className="stat-label">Total Quotations</span>
          </div>
        </div>
        <div className="stat-box slate">
          <div className="stat-icon"><CheckCircle size={20} /></div>
          <div className="stat-info">
            <span className="stat-value">{quotations.filter(q => q.status === 'approved').length}</span>
            <span className="stat-label">Approved</span>
          </div>
        </div>
        <div className="stat-box indigo">
          <div className="stat-icon"><Send size={20} /></div>
          <div className="stat-info">
            <span className="stat-value">{quotations.filter(q => q.status === 'sent').length}</span>
            <span className="stat-label">Sent</span>
          </div>
        </div>
      </div>

      <div className="toolbar">
        <div className="search-box">
          <Search size={16} />
          <input type="text" placeholder="Search quotations..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} />
        </div>
      </div>

      <div className="table-container">
        <table className="data-table">
          <thead>
            <tr>
              <th>Quote #</th>
              <th>Date</th>
              <th>Client</th>
              <th>Items</th>
              <th>Amount</th>
              <th>Valid Until</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? <tr><td colSpan="8" className="text-center">Loading...</td></tr> :
             filteredQuotations.length === 0 ? <tr><td colSpan="8" className="text-center">No quotations found</td></tr> :
             filteredQuotations.map(quotation => (
              <tr key={quotation._id}>
                <td><span className="quote-badge">{quotation.quotationNumber}</span></td>
                <td>{formatDate(quotation.quotation_date || quotation.date)}</td>
                <td><strong>{quotation.clientName}</strong></td>
                <td>{quotation.items?.length || 0} item(s)</td>
                <td className="amount-cell">{formatCurrency(quotation.total_amount || quotation.totalAmount)}</td>
                <td>{formatDate(quotation.validUntil)}</td>
                <td>
                  <select value={quotation.status} onChange={(e) => handleStatusChange(quotation._id, e.target.value)} className={`status-select status-${quotation.status}`}>
                    <option value="draft">Draft</option>
                    <option value="sent">Sent</option>
                    <option value="approved">Approved</option>
                    <option value="rejected">Rejected</option>
                    <option value="expired">Expired</option>
                  </select>
                </td>
                <td className="actions">
                  <button className="btn-icon" onClick={() => handlePrint(quotation)} title="Print"><Printer size={14} /></button>
                  <button className="btn-icon" onClick={() => handleEdit(quotation)} title="Edit"><Edit size={14} /></button>
                  <button className="btn-icon" onClick={() => handleDelete(quotation._id)} title="Delete"><Trash2 size={14} /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal-content wide-modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{editingQuotation ? 'Edit' : 'New'} Quotation</h3>
              <button className="btn-close" onClick={() => setShowModal(false)}><X size={20} /></button>
            </div>
            <form onSubmit={handleSubmit} className="module-form">
              <div className="form-row">
                <div className="form-group">
                  <label>Quotation Number *</label>
                  <input type="text" value={formData.quotationNumber} onChange={e => setFormData({...formData, quotationNumber: e.target.value})} placeholder="e.g. QT-001" required />
                </div>
                <div className="form-group">
                  <label>Date *</label>
                  <input type="date" value={formData.date} onChange={e => setFormData({...formData, date: e.target.value})} required />
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Client Name *</label>
                  <input type="text" value={formData.clientName} onChange={e => setFormData({...formData, clientName: e.target.value})} placeholder="Client name" required />
                </div>
                <div className="form-group">
                  <label>Client PIN</label>
                  <input type="text" value={formData.clientPin} onChange={e => setFormData({...formData, clientPin: e.target.value})} placeholder="KRA PIN" />
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Valid Until</label>
                  <input type="date" value={formData.validUntil} onChange={e => setFormData({...formData, validUntil: e.target.value})} />
                </div>
                <div className="form-group">
                  <label>Tax Rate (%)</label>
                  <input type="number" value={formData.taxRate} onChange={e => setFormData({...formData, taxRate: e.target.value})} min="0" max="100" />
                </div>
              </div>

              <div className="items-section">
                <h4>Line Items</h4>
                {formData.items.map((item, idx) => (
                  <div key={idx} className="item-row">
                    <input type="text" placeholder="Description" value={item.description} onChange={e => handleItemChange(idx, 'description', e.target.value)} required />
                    <input type="number" placeholder="Qty" value={item.quantity} onChange={e => handleItemChange(idx, 'quantity', e.target.value)} min="1" required />
                    <input type="number" placeholder="Rate" value={item.rate} onChange={e => handleItemChange(idx, 'rate', e.target.value)} min="0" step="0.01" required />
                    <span className="item-amount">{formatCurrency(item.quantity * item.rate)}</span>
                    <button type="button" className="btn-remove" onClick={() => removeItem(idx)} disabled={formData.items.length===1}>×</button>
                  </div>
                ))}
                <button type="button" className="btn-add-item" onClick={addItem}>+ Add Item</button>
              </div>

              <div className="totals-section">
                <div className="total-row"><span>Subtotal:</span><span>{formatCurrency(subtotal)}</span></div>
                <div className="total-row"><span>Tax ({formData.taxRate}%):</span><span>{formatCurrency(taxAmount)}</span></div>
                <div className="total-row grand-total"><span>Total:</span><span>{formatCurrency(total)}</span></div>
              </div>

              <div className="form-group">
                <label>Description</label>
                <textarea value={formData.description} onChange={e => setFormData({...formData, description: e.target.value})} rows="2" placeholder="Quotation description..." />
              </div>

              <div className="modal-actions">
                <button type="button" className="btn btn-secondary" onClick={() => setShowModal(false)}>Cancel</button>
                <button type="submit" className="btn btn-primary" disabled={loading}>{loading ? 'Saving...' : (editingQuotation ? 'Update' : 'Create')}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default QuotationModule;
